<script setup>
/* global axios */
import { computed, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import {
  contactsPolicy,
  contactsScope,
  normalizeContactPhone,
  conversationRoute,
} from './policy';
import {
  nativeContactsApi,
  prepareContact,
  prepareConversation,
} from './workflow';

const route = useRoute();
const router = useRouter();
const store = useStore();
const dialog = ref(null);
const name = ref('');
const phone = ref('');
const busy = ref(false);
const error = ref('');
const savedContact = ref(null);
const savedConversation = ref(null);
const reused = ref(false);
const contactUnknown = ref(false);
const conversationUnknown = ref(false);
const generation = ref(0);
const copy = contactsPolicy.copy;

const scope = computed(() => {
  const inbox = store.getters['inboxes/getInbox'](
    Number(route.params.inbox_id)
  );
  return contactsScope(route.params.accountId, inbox);
});
const normalizedPhone = computed(() => normalizeContactPhone(phone.value));
const errorMessage = computed(
  () => copy.errors[error.value] || copy.errors.response_mismatch
);
const formInvalid = computed(
  () => !normalizedPhone.value || name.value.trim().length > 255
);

watch(scope, (next, previous) => {
  if (
    next?.accountId !== previous?.accountId ||
    next?.inboxId !== previous?.inboxId
  ) {
    generation.value += 1;
    dialog.value?.close();
    savedContact.value = null;
    savedConversation.value = null;
    contactUnknown.value = false;
    conversationUnknown.value = false;
    name.value = '';
    phone.value = '';
    error.value = '';
  }
});

onUnmounted(() => {
  generation.value += 1;
});

const open = () => {
  if (!scope.value || busy.value) return;
  // Preserve a committed/unknown operation across close and reopen. Changing
  // the selected inbox invalidates it instead of moving it to another account.
  if (!savedContact.value && !contactUnknown.value) {
    name.value = '';
    phone.value = '';
    error.value = '';
  }
  dialog.value.open();
};

const saveAndOpen = async () => {
  if (!scope.value || busy.value || formInvalid.value) return;
  const captured = { ...scope.value };
  const operation = generation.value;
  const capturedPath = route.fullPath;
  const isCurrent = () =>
    operation === generation.value &&
    capturedPath === route.fullPath &&
    captured.accountId === scope.value?.accountId &&
    captured.inboxId === scope.value?.inboxId;
  const api = nativeContactsApi(captured, axios);
  busy.value = true;
  error.value = '';
  try {
    if (!savedContact.value) {
      const result = await prepareContact({
        api,
        phone: normalizedPhone.value,
        name: name.value,
        isCurrent,
        existingOnly: contactUnknown.value,
      });
      savedContact.value = result.contact;
      reused.value = result.reused;
    }
    if (!savedConversation.value) {
      savedConversation.value = await prepareConversation({
        api,
        scope: captured,
        contact: savedContact.value,
        isCurrent,
        existingOnly: conversationUnknown.value,
      });
    }
    if (!isCurrent()) return;
    const result = await router.push(
      conversationRoute(captured, savedConversation.value.id)
    );
    if (result) throw new Error('navigation_failed');
    dialog.value?.close();
    savedContact.value = null;
    savedConversation.value = null;
    contactUnknown.value = false;
    conversationUnknown.value = false;
    name.value = '';
    phone.value = '';
  } catch (failure) {
    if (!isCurrent()) return;
    const code = failure.code || failure.message;
    error.value = copy.errors[code] ? code : 'response_mismatch';
    contactUnknown.value ||= code === 'contact_unknown';
    conversationUnknown.value ||= code === 'conversation_unknown';
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <div class="contents">
    <Button
      v-if="scope"
      :label="copy.add_contacts"
      icon="i-lucide-user-plus"
      xs
      :disabled="busy"
      data-testid="whatsapp-add-contacts"
      @click="open"
    />
    <Dialog
      v-if="scope"
      ref="dialog"
      :title="copy.title"
      width="md"
      @confirm="saveAndOpen"
    >
      <div class="flex flex-col gap-4 p-1" data-testid="whatsapp-contact-form">
        <Input
          v-model="name"
          data-testid="contact-name-input"
          :label="copy.name"
          :placeholder="copy.name_placeholder"
          :disabled="busy || Boolean(savedContact) || contactUnknown"
        />
        <div class="flex flex-col gap-2">
          <Input
            v-model="phone"
            type="tel"
            inputmode="tel"
            autocomplete="tel"
            data-testid="contact-phone-input"
            :label="copy.phone"
            :placeholder="copy.phone_placeholder"
            :disabled="busy || Boolean(savedContact) || contactUnknown"
          />
          <p
            v-if="phone && !normalizedPhone"
            class="text-sm text-n-ruby-11"
            role="alert"
          >
            {{ copy.errors.invalid_phone }}
          </p>
        </div>
        <p class="text-sm text-n-slate-11">{{ copy.first_message }}</p>
        <p v-if="savedContact" class="text-sm text-n-slate-12" role="status">
          {{ reused ? copy.existing_contact : copy.saved_contact }}
        </p>
        <p v-if="error" class="text-sm text-n-ruby-11" role="alert">
          {{ errorMessage }}
        </p>
      </div>
      <template #footer>
        <div class="flex items-center justify-between w-full gap-3">
          <Button
            :label="copy.cancel"
            variant="link"
            :disabled="busy"
            @click="dialog.close()"
          />
          <Button
            type="submit"
            :label="savedContact ? copy.open_existing : copy.save_open"
            :disabled="busy || formInvalid"
            :is-loading="busy"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>
